// Orquesta los visuales del Enlace Geométrico — beams + sealing rings.
//
// 2026-05-05 — Removidos IsolationRig y TelemetryHologram 3D. Antes la
// fase sealed encerraba la cámara en un fog ultra denso (near=4, far=16)
// que en escritorio se veía como un "recuadro negro gigante" detrás del
// holograma. Decisión nueva: la información del peer (silicon + 6
// pilares) se ve en un panel 2D al hacer tap a cualquier avatar. Los
// haces y el anillo de sellado siguen siendo 3D — eso es ritual del
// enlace; pero la "intimidad" no se logra con fog cinemático sino con
// el panel 2D enfocado.

import { useFrame } from "@react-three/fiber"
import { useRef, useState } from "react"

import { useLinkStore } from "../../stores/useLinkStore"
import { useNetworkStore } from "../../stores/useNetworkStore"
import { usePlayerStore } from "../../stores/usePlayerStore"
import { LinkBeam } from "./LinkBeam"
import { SealingRing } from "./SealingRing"

const SEAL_DURATION_MS = 3000

export function LinkScene() {
    const phase = useLinkStore((s) => s.phase)
    const myReachTarget = useLinkStore((s) => s.myReachTarget)
    const incomingReaches = useLinkStore((s) => s.incomingReaches)
    const peerId = useLinkStore((s) => s.peerId)
    const sealStartTs = useLinkStore((s) => s.sealStartTs)

    const myPos = usePlayerStore((s) => s.position)
    const peers = useNetworkStore((s) => s.peers)

    // Progreso del sealing — animado en cada frame para que el anillo crezca suave.
    const [sealProgress, setSealProgress] = useState(0)
    const lastUpdate = useRef(0)
    useFrame(() => {
        if (phase !== "sealing" || sealStartTs == null) {
            if (sealProgress !== 0) setSealProgress(0)
            return
        }
        const elapsed = Date.now() - sealStartTs
        const p = Math.min(1, elapsed / SEAL_DURATION_MS)
        // Throttle re-render (cada 50ms basta para 60fps de animación visual).
        const now = performance.now()
        if (now - lastUpdate.current > 50) {
            lastUpdate.current = now
            setSealProgress(p)
        }
    })

    const myXZ: [number, number, number] = [myPos[0], 0, myPos[2]]

    // ── Fase: reaching (yo → target) ─────────────────────────────────
    const myTargetPeer =
        myReachTarget && peers.get(myReachTarget)
    const myTargetPos: [number, number, number] | null = myTargetPeer
        ? [myTargetPeer.position[0], 0, myTargetPeer.position[2]]
        : null

    // ── Fase: incoming (otros → yo) ──────────────────────────────────
    const incomingArr = Array.from(incomingReaches)
        .map((id) => peers.get(id))
        .filter((p): p is NonNullable<typeof p> => Boolean(p))

    // ── Fase: sealing/sealed peer ────────────────────────────────────
    const sealedPeer = peerId ? peers.get(peerId) : null
    const sealedPeerPos: [number, number, number] | null = sealedPeer
        ? [sealedPeer.position[0], 0, sealedPeer.position[2]]
        : null

    return (
        <>
            {/* Haz desde mí hacia mi target (mientras pineo) */}
            {myTargetPos && (
                <LinkBeam
                    from={myXZ}
                    to={myTargetPos}
                    color="#00E5FF"
                    completion={phase === "reaching" ? 0.6 : 1}
                />
            )}

            {/* Haces de cada uno que me pinea hacia mí */}
            {incomingArr.map((peer) => {
                const fromPos: [number, number, number] = [
                    peer.position[0],
                    0,
                    peer.position[2],
                ]
                // Si yo le respondo a este, ya está cubierto por el sealedPeer beam.
                if (peer.id === peerId) return null
                return (
                    <LinkBeam
                        key={peer.id}
                        from={fromPos}
                        to={myXZ}
                        color="#00E5FF"
                        completion={0.6}
                    />
                )
            })}

            {/* Si estoy en sealing/sealed con un peer: haz también de él hacia mí */}
            {sealedPeerPos && phase !== "idle" && phase !== "reaching" && (
                <LinkBeam
                    from={sealedPeerPos}
                    to={myXZ}
                    color="#00E5FF"
                    completion={1}
                />
            )}

            {/* Anillos de carga durante el sealing — uno en cada avatar */}
            {phase === "sealing" && sealedPeerPos && (
                <>
                    <SealingRing position={myXZ} progress={sealProgress} />
                    <SealingRing
                        position={sealedPeerPos}
                        progress={sealProgress}
                    />
                </>
            )}

            {/* Holograma 3D removido (2026-05-05). La telemetría del peer
                ahora se ve en un panel 2D (PeerInfoPanel) al hacer tap a
                cualquier avatar. Cuando se sella un enlace, App.tsx abre
                automáticamente el panel del peer sellado. */}
        </>
    )
}
